import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { Invoice, InvoiceLineItem, UpdateInvoiceRequest } from '@stfors/shared-types';
import { Modal } from './Modal';
import { MoneyInput } from './MoneyInput';
import { DateInput } from './DateInput';
import { ApiError } from '../api/client';
import { useUpdateInvoice } from '../api/queries/invoices';
import { formatMoney } from '../utils/money';
import './EditInvoiceModal.css';
import { dueDateFor } from '../utils/invoiceTerms';

interface Props {
  open: boolean;
  invoice: Invoice & { line_items: InvoiceLineItem[] };
  onClose: () => void;
}

interface LineRow {
  description: string;
  quantity: string;
  unit_amount: string;
}

const isSourceLine = (li: InvoiceLineItem) => li.time_entry_id != null || li.expense_id != null;
const blankLine = (): LineRow => ({ description: '', quantity: '1', unit_amount: '0.00' });

function lineTotal(quantity: string, unit: string): number {
  const q = Number(quantity);
  const u = Number(unit);
  if (!Number.isFinite(q) || !Number.isFinite(u)) return 0;
  return Math.round(q * u * 100) / 100;
}

export function EditInvoiceModal({ open, invoice, onClose }: Props): JSX.Element {
  const update = useUpdateInvoice();

  const [number, setNumber] = useState('');
  const [issueDate, setIssueDate] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState<number | null>(null);
  // Same Net 30 follow as the builder — until the due date is set by hand,
  // moving the issue date carries it along. An invoice being EDITED opens with
  // a stored due date, but that was composed rather than typed here, so it
  // starts untouched and only sticks once someone changes it in this session.
  const [dueTouched, setDueTouched] = useState(false);
  const [tax, setTax] = useState('0.00');
  const [notes, setNotes] = useState('');
  const [manualLines, setManualLines] = useState<LineRow[]>([]);
  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const sourceLines = useMemo(
    () => invoice.line_items.filter(isSourceLine),
    [invoice.line_items],
  );

  useEffect(() => {
    if (!open) return;
    setNumber(invoice.invoice_number);
    setIssueDate(invoice.issue_date);
    setDueDate(invoice.due_date);
    setDueTouched(false);
    setTax(invoice.tax_amount);
    setNotes(invoice.notes ?? '');
    setManualLines(
      invoice.line_items
        .filter((li) => !isSourceLine(li))
        .map((li) => ({ description: li.description, quantity: li.quantity, unit_amount: li.unit_amount })),
    );
    setTopError(null);
    setFieldErrors({});
  }, [open, invoice]);

  const setLine = (i: number, patch: Partial<LineRow>) =>
    setManualLines((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addLine = () => setManualLines((rows) => [...rows, blankLine()]);
  const removeLine = (i: number) => setManualLines((rows) => rows.filter((_, idx) => idx !== i));

  // Live preview of the new totals.
  const sourceSubtotal = sourceLines.reduce((s, li) => s + Number(li.line_total), 0);
  const manualSubtotal = manualLines.reduce((s, l) => s + lineTotal(l.quantity, l.unit_amount), 0);
  const subtotal = Math.round((sourceSubtotal + manualSubtotal) * 100) / 100;
  const total = Math.round((subtotal + Number(tax || 0)) * 100) / 100;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    if (number.trim().length === 0) {
      setFieldErrors({ invoice_number: 'Invoice number is required' });
      return;
    }
    if (issueDate == null || dueDate == null) {
      setTopError('Issue and due dates are required.');
      return;
    }
    if (dueDate < issueDate) {
      setFieldErrors({ due_date: 'Due date must be on or after the issue date' });
      return;
    }
    for (let i = 0; i < manualLines.length; i++) {
      const l = manualLines[i]!;
      if (l.description.trim().length === 0) {
        setFieldErrors({ [`line_${i}`]: 'Description is required' });
        return;
      }
      if (!(Number(l.quantity) > 0)) {
        setFieldErrors({ [`line_${i}`]: 'Quantity must be greater than 0' });
        return;
      }
    }
    if (sourceLines.length + manualLines.length === 0) {
      setTopError('An invoice needs at least one line item.');
      return;
    }

    const payload: UpdateInvoiceRequest = {
      invoice_number: number.trim(),
      issue_date: issueDate,
      due_date: dueDate,
      tax_amount: tax || '0',
      notes: notes.trim() || null,
      lines: manualLines.map((l) => ({
        description: l.description.trim(),
        quantity: l.quantity,
        unit_amount: l.unit_amount,
      })),
    };

    try {
      await update.mutateAsync({ id: invoice.id, ...payload });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setFieldErrors({ invoice_number: 'That invoice number is already in use' });
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { error?: string } | null;
        setTopError(body?.error ?? 'Please check the invoice fields.');
        return;
      }
      setTopError('Could not save the invoice. Try again.');
    }
  };

  const busy = update.isPending;

  return (
    <Modal
      title={`Edit Invoice — ${invoice.invoice_number}`}
      open={open}
      onClose={onClose}
      busy={busy}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="edit-invoice-form" className="btn-cyan-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <form id="edit-invoice-form" className="eim-form" onSubmit={onSubmit} noValidate>
        {invoice.status !== 'draft' && (
          <p className="eim-note">
            This invoice is <strong>{invoice.status}</strong>. Editing its amounts will re-check the
            balance against any recorded payments.
          </p>
        )}

        <div className="eim-row">
          <label className="eim-field">
            <span>Invoice #</span>
            <input
              type="text"
              className="form-input"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              maxLength={100}
            />
            {fieldErrors['invoice_number'] && <span className="field-error">{fieldErrors['invoice_number']}</span>}
          </label>
          <label className="eim-field">
            <span>Issue date</span>
            <DateInput
              value={issueDate}
              onChange={(next) => {
                setIssueDate(next);
                if (!dueTouched) setDueDate(dueDateFor(next));
              }}
            />
          </label>
          <label className="eim-field">
            <span>Due date</span>
            <DateInput
              value={dueDate}
              onChange={(next) => { setDueTouched(true); setDueDate(next); }}
            />
            {fieldErrors['due_date'] && <span className="field-error">{fieldErrors['due_date']}</span>}
          </label>
        </div>

        {sourceLines.length > 0 && (
          <div className="eim-source">
            <div className="eim-source-head">From time / expenses (edit via the builder or void)</div>
            {sourceLines.map((li) => (
              <div key={li.id} className="eim-source-row">
                <span className="eim-source-desc">{li.description}</span>
                <span className="eim-source-amt">{formatMoney(li.line_total)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="eim-lines">
          <div className="eim-lines-head">
            <span>Description</span>
            <span className="eim-numeric">Qty</span>
            <span className="eim-numeric">Unit</span>
            <span className="eim-numeric">Total</span>
            <span />
          </div>
          {manualLines.map((l, i) => (
            <div key={i} className="eim-line">
              <input
                className="form-input"
                type="text"
                value={l.description}
                onChange={(e) => setLine(i, { description: e.target.value })}
                placeholder="Description"
                maxLength={500}
              />
              <input
                className="form-input eim-qty"
                type="text"
                inputMode="decimal"
                value={l.quantity}
                onChange={(e) => setLine(i, { quantity: e.target.value })}
              />
              <MoneyInput value={l.unit_amount} onChange={(v) => setLine(i, { unit_amount: v })} allowNegative />
              <span className="eim-numeric eim-line-total">{formatMoney(lineTotal(l.quantity, l.unit_amount).toFixed(2))}</span>
              <button
                type="button"
                className="eim-remove"
                onClick={() => removeLine(i)}
                aria-label="Remove line"
                title="Remove line"
              >
                ×
              </button>
              {fieldErrors[`line_${i}`] && <span className="field-error eim-line-error">{fieldErrors[`line_${i}`]}</span>}
            </div>
          ))}
          <button type="button" className="btn-charcoal-secondary eim-add" onClick={addLine}>
            + Add line
          </button>
        </div>

        <div className="eim-totals">
          <div className="eim-total-row">
            <label className="eim-field eim-tax">
              <span>Tax</span>
              <MoneyInput value={tax} onChange={setTax} />
            </label>
          </div>
          <div className="eim-total-row"><span>Subtotal</span><span>{formatMoney(subtotal.toFixed(2))}</span></div>
          <div className="eim-total-row eim-total-grand"><span>Total</span><span>{formatMoney(total.toFixed(2))}</span></div>
        </div>

        <label className="eim-field">
          <span>Notes</span>
          <textarea
            className="form-input eim-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={10_000}
          />
        </label>

        {topError && <p className="eim-error-banner" role="alert">{topError}</p>}
      </form>
    </Modal>
  );
}
